import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Loader2, Calendar, MapPin, Download, ExternalLink } from 'lucide-react';
import api from '@/utils/api';
import { usePixels } from '@/contexts/PixelContext';

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 15; // ~30s

const PaymentSuccessPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id') || '';
  const [attempts, setAttempts] = useState(0);
  const [status, setStatus] = useState('polling'); // polling | paid | gate_pending | timeout | error
  const [txn, setTxn] = useState(null);
  const { trackEvent } = usePixels();
  const firedRef = React.useRef(false);

  // Bundle 55 - hard-fail-safe. If the gate is still open post-payment
  // we MUST NOT flash "Payment confirmed, your ticket is on its way"
  // because the ticket isn't (and won't) be issued until the user
  // adds a photo + social. Bundle 51 shipped the redirect but kept
  // the misleading interstitial visible for 900ms; user testing
  // showed people took that as "done" and never noticed the gate.
  useEffect(() => {
    if (status !== 'paid' || !txn?.registration_id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/rsvp/${txn.registration_id}/profile-status`,
        );
        if (cancelled) return;
        if (data?.gate_required && !data?.profile_complete) {
          setStatus('gate_pending');
        }
      } catch (_) { /* stay on success page if the status call blew up */ }
    })();
    return () => { cancelled = true; };
  }, [status, txn]);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    let cancelled = false;

    const poll = async (n) => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setTxn(data);
        if (data?.payment_status === 'paid') {
          setStatus('paid');
          // Bundle 60: FE-side Purchase fire REMOVED. The server-side
          // `dispatch_purchase` in payment_routes.py::stripe_webhook is
          // now the single authoritative Purchase dispatch site for
          // ALL payment methods (Stripe + Zelle + Venmo + PayPal),
          // firing off the deterministic event_id
          // `purchase_{transaction.id}`. Keeping a FE fire here would
          // double-count (Meta dedups by event_id but our own
          // `pixel_event_logs` coverage card would inflate). We keep
          // CompleteRegistration on the FE because that is unrelated
          // to server-side payment capture.
          if (!firedRef.current) {
            firedRef.current = true;
            try {
              trackEvent('CompleteRegistration', {
                content_name: data?.event?.title || 'event',
                content_ids: [String(data?.registration_id || '')],
                status: 'paid',
              });
            } catch (_) { /* never break the flow */ }
          }
          return;
        }
      } catch (e) { /* transient */ }
      if (n + 1 >= MAX_ATTEMPTS) { setStatus('timeout'); return; }
      setAttempts(n + 1);
      setTimeout(() => poll(n + 1), POLL_INTERVAL_MS);
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId, trackEvent]);

  const event = txn?.event;
  const regId = txn?.registration_id;
  const ticketCode = txn?.ticket_code;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50/40 via-white to-secondary-50/30 py-16 px-4">
      <div className="max-w-xl mx-auto">
        <Card className="border-neutral-100 shadow-lg">
          <CardContent className="p-8 md:p-10">
            {status === 'polling' && (
              <div className="text-center space-y-4" data-testid="payment-polling">
                <Loader2 className="h-12 w-12 text-primary-700 animate-spin mx-auto" />
                <h1 className="font-display text-2xl md:text-3xl font-bold text-primary-900">
                  Confirming your payment...
                </h1>
                <p className="text-neutral-600">Hang tight - this usually takes a few seconds.</p>
                <p className="text-xs text-neutral-400">Attempt {attempts + 1} of {MAX_ATTEMPTS}</p>
              </div>
            )}
            {status === 'gate_pending' && (
              <div className="space-y-6 text-center" data-testid="payment-gate-pending">
                <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-secondary-100 to-secondary-50 border border-secondary-200 shadow-gold">
                  <CheckCircle2 className="h-8 w-8 text-secondary-700" />
                </div>
                <h1 className="font-display text-2xl md:text-3xl font-bold text-primary-900">
                  Payment received. One more step.
                </h1>
                <p className="text-neutral-600 leading-relaxed">
                  Your payment is confirmed, but your ticket will not be
                  issued until you add one photo and one social account
                  to your Dostination profile. Organizers use these to
                  welcome you at the door.
                </p>
                <Button
                  onClick={() => navigate(`/rsvp/${txn?.registration_id}/complete-profile`, { replace: true })}
                  className="w-full bg-primary-800 hover:bg-primary-900 text-white"
                  data-testid="payment-gate-pending-continue-btn"
                  size="lg"
                >
                  Complete your profile to get your ticket
                </Button>
                <p className="text-xs text-neutral-500">
                  Takes about a minute. We saved everything else.
                </p>
              </div>
            )}
            {status === 'paid' && (
              <div className="space-y-6" data-testid="payment-success">
                <div className="text-center space-y-3">
                  <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-secondary-100 to-secondary-50 border border-secondary-200 shadow-gold">
                    <CheckCircle2 className="h-8 w-8 text-secondary-700" />
                  </div>
                  <h1 className="font-display text-2xl md:text-3xl font-bold text-primary-900">Payment confirmed</h1>
                  <p className="text-neutral-600">Your spot is officially booked. A confirmation email with your ticket is on its way.</p>
                </div>

                {event && (
                  <div className="rounded-xl border border-neutral-100 p-4 bg-neutral-50 space-y-2">
                    <div className="font-semibold text-primary-900">{event.title}</div>
                    {event.start_datetime && (
                      <div className="flex items-center gap-2 text-sm text-neutral-600">
                        <Calendar className="h-4 w-4" /> {new Date(event.start_datetime).toLocaleString()}
                      </div>
                    )}
                    {(event.venue_name || event.address_line) && (
                      <div className="flex items-start gap-2 text-sm text-neutral-600">
                        <MapPin className="h-4 w-4 mt-0.5" />
                        <div>
                          {event.venue_name && <div>{event.venue_name}</div>}
                          {event.address_line && <div className="text-xs text-neutral-500">{event.address_line}</div>}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {txn?.registration_id && (
                  <div className="rounded-xl border border-neutral-100 p-4 bg-white text-center space-y-3">
                    <div className="text-xs uppercase tracking-widest text-neutral-500">Your ticket</div>
                    {txn.ticket_code && (
                      <div className="font-mono text-xl font-bold text-secondary-600 tracking-wider">
                        {txn.ticket_code}
                      </div>
                    )}
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/tickets/${txn.registration_id}/qr.png`}
                      alt="QR ticket"
                      className="w-48 h-48 mx-auto rounded-lg"
                      data-testid="payment-qr"
                    />
                    <div className="text-xs text-neutral-500">Present this QR at check-in.</div>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/tickets/${txn.registration_id}/ticket.pdf`}
                      className="inline-flex items-center gap-2 text-sm text-primary-700 hover:text-primary-900 font-semibold"
                      data-testid="payment-download-pdf"
                    >
                      <Download className="h-4 w-4" /> Download PDF ticket
                    </a>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Button asChild variant="outline" className="w-full" data-testid="payment-my-events-btn">
                    <Link to="/my-events">View my events</Link>
                  </Button>
                  <Button asChild className="w-full bg-primary-800 hover:bg-primary-900 text-white">
                    <Link to="/events">Back to events</Link>
                  </Button>
                </div>
              </div>
            )}
            {status === 'timeout' && (
              <div className="text-center space-y-4" data-testid="payment-timeout">
                <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-secondary-50 border border-secondary-200">
                  <Loader2 className="h-6 w-6 text-secondary-700" />
                </div>
                <h1 className="font-display text-2xl font-bold text-primary-900">Your payment is processing</h1>
                <p className="text-neutral-600">We&apos;ll email you the moment it clears. This usually takes just a few minutes.</p>
                <Button asChild variant="outline"><Link to="/my-events">Go to my events</Link></Button>
              </div>
            )}
            {status === 'error' && (
              <div className="text-center space-y-4">
                <h1 className="font-display text-2xl font-bold text-primary-900">Missing session</h1>
                <p className="text-neutral-600">We couldn&apos;t find a payment session. If you were charged, please contact your organizer.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
